import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next'; // 🎯 الترجمة
import { Search, Plus, List, ChevronRight, ChevronLeft, RefreshCw } from 'lucide-react';

const DomainsList = () => {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    
    // States لبيانات الجدول والباجنيشن
    const [domains, setDomains] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [searchTerm, setSearchTerm] = useState('');
    const [skip, setSkip] = useState(0);
    const limit = 20; // عدد السجلات في الصفحة

    const fetchDomains = useCallback(async () => {
        setLoading(true);
        try {
            // نبعت الطلب للسيرفر مع الفلترة والباجنيشن
            const res = await axios.get(`/api/domains?skip=${skip}&limit=${limit}&search=${searchTerm}`);
            setDomains(res.data.data);
            setTotal(res.data.total);
        } catch (err) {
            console.error("Error fetching domains", err);
        } finally {
            setLoading(false);
        }
    }, [skip, searchTerm]);

    useEffect(() => {
        const delayDebounceFn = setTimeout(() => {
            fetchDomains();
        }, 500); // Debounce للبحث عشان ما نضغطش على السيرفر
        return () => clearTimeout(delayDebounceFn);
    }, [fetchDomains]);

    const isRTL = i18n.language === 'ar' || i18n.language === 'ur';

    return (
        <div className="flex flex-col h-full bg-[#f3f4f6] text-[11px]">
            {/* Toolbar */}
            <div className="bg-white p-2 border-b flex justify-between items-center shadow-sm">
                <div className="flex items-center gap-4">
                    <h2 className="font-bold text-gray-700 uppercase ml-4">{t('menu.classifications')}</h2>
                    <div className="relative flex items-center border-b border-gray-300 focus-within:border-blue-500">
                        <input 
                            placeholder={t('domains.find_placeholder')} 
                            className={`pl-2 pr-8 py-1 outline-none bg-transparent w-64 ${isRTL ? 'text-right' : 'text-left'}`}
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setSkip(0); }}
                        />
                        <Search size={14} className={`absolute ${isRTL ? 'left-2' : 'right-2'} text-gray-400`} />
                    </div>
                    {loading && <RefreshCw size={14} className="animate-spin text-blue-500" />}
                </div>
                <button 
                    onClick={() => navigate('/domains/new')}
                    className="flex items-center gap-2 bg-[#3c70a4] text-white px-4 py-1.5 rounded font-bold hover:bg-[#2a507a] transition-all"
                >
                    <Plus size={14}/> {t('domains.new_domain')}
                </button>
            </div>

            {/* Table Area */}
            <div className="p-4 flex-1 overflow-auto">
                <div className="bg-white border border-gray-300 shadow-sm rounded-sm overflow-hidden">
                    <table className="w-full border-collapse">
                        <thead className="bg-gray-100 text-gray-600 uppercase font-bold border-b">
                            <tr>
                                <th className="p-3 text-left w-10"></th>
                                <th className={`p-3 w-48 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{t('domains.domain_id')}</th>
                                <th className={`p-3 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{t('domains.description')}</th>
                                <th className="p-3 text-center w-32 border-r">{t('domains.type')}</th>
                                <th className="p-3 text-center w-24">{t('domains.values')}</th>
                            </tr>
                        </thead>
                        <tbody className={loading ? 'opacity-50' : ''}>
                            {domains.length === 0 && !loading ? (
                                <tr>
                                    <td colSpan="5" className="p-10 text-center text-gray-400 font-bold uppercase italic">
                                        {t('dashboard.no_data')}
                                    </td>
                                </tr>
                            ) : (
                                domains.map((domain) => (
                                    <tr 
                                        key={domain.domain_id} 
                                        onClick={() => navigate(`/domains/${domain.domain_id}`)}
                                        className="border-b hover:bg-blue-50 cursor-pointer transition-colors group"
                                    >
                                        <td className="p-3 text-center">
                                            <ChevronRight size={14} className={`text-gray-300 group-hover:text-blue-600 ${isRTL ? 'rotate-180' : ''}`}/>
                                        </td>
                                        <td className={`p-3 font-bold text-blue-900 border-r ${isRTL ? 'text-right' : 'text-left'}`}>{domain.domain_id}</td>
                                        <td className={`p-3 border-r text-gray-600 ${isRTL ? 'text-right' : 'text-left'}`}>{domain.description}</td>
                                        <td className="p-3 text-center border-r uppercase text-[10px] font-black text-gray-500">{domain.domain_type || 'SYNONYM'}</td>
                                        <td className="p-3 text-center">
                                            <span className="bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full font-black text-[9px]">
                                                {domain.values?.length || 0}
                                            </span>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>

                {/* 🎯 Pagination Controls */}
                <div className={`mt-4 flex justify-between items-center px-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                    <div className="text-gray-500 font-bold">
                        {t('dashboard.showing')} {skip + 1} - {Math.min(skip + limit, total)} {t('dashboard.of')} {total}
                    </div>
                    
                    <div className={`flex gap-2 ${isRTL ? 'flex-row-reverse' : ''}`}>
                        {/* زرار السابق (Previous) */}
                        <button 
                            disabled={skip === 0}
                            onClick={() => setSkip(prev => Math.max(0, prev - limit))}
                            className="p-2 bg-white border border-gray-300 rounded shadow-sm disabled:opacity-30 hover:bg-gray-50 transition-transform active:scale-90"
                            title={t('dashboard.previous')}
                        >
                            {/* في الـ RTL، السهم اللي باصص يمين هو اللي بيرجع لورا */}
                            {isRTL ? <ChevronRight size={16} /> : <ChevronLeft size={16} />}
                        </button>

                        {/* زرار التالي (Next) */}
                        <button 
                            disabled={skip + limit >= total}
                            onClick={() => setSkip(prev => prev + limit)}
                            className="p-2 bg-white border border-gray-300 rounded shadow-sm disabled:opacity-30 hover:bg-gray-50 transition-transform active:scale-90"
                            title={t('dashboard.next')}
                        >
                            {/* في الـ RTL، السهم اللي باصص شمال هو اللي بيمشي لقدام */}
                            {isRTL ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default DomainsList;